import React from 'react';
import {makeStyles} from '@material-ui/styles';
import Paper from '@material-ui/core/Paper';
import pageSizes from './sidebars/PrintSidebar/pageSizes';
import {PrintPreviewAttribution} from './PrintPreviewAttribution';
import Compass from './Compass';
import theme from '../../theme';
import {Typography} from '@material-ui/core';
import getMagneticDeclination from '../../utils/getMagneticDeclination';
import {pageMargin} from './sidebars/PrintSidebar/generatePDFMap';

const PPI = 96;

function inToPt(inches: number) {
    return inches * PPI;
}
function pxToPt(px: number) {
    return px * 0.75;
}
function ptToPx(pt: number) {
    return pt / 0.75;
}

const pagePadding = inToPt(pageMargin);

interface PrintPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
    sidebar: MapSidebar;
    layers: Layer[] | undefined;
    center: mapboxgl.LngLat | undefined;
    zoom: number | undefined;
    layerTypes: Record<string, LayerType> | undefined;
    map: mapboxgl.Map | undefined;
}

const useStyles = makeStyles({
    background: {
        height: `calc(100% - ${theme.spacing(8)}px)`,
        width: `calc(100% - ${theme.spacing(8)}px)`,
        padding: theme.spacing(4),
        backgroundColor: '#eeeeee',
    },
    printPaper: {
        transformOrigin: 'left top',
    },
    paperInner: {
        padding: `${pagePadding}pt`,
        height: '100%',
        width: '100%',
        position: 'relative',
        boxSizing: 'border-box',
    },
    bottomBar: {
        width: 'calc(100% - 6px)',
        height: '25pt',
        margin: '3px',
        marginTop: '-27pt',
        bottom: 0,
        position: 'relative',
        pointerEvents: 'none',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        zIndex: 2,
    },
    sources: {
        float: 'right',
        position: 'absolute',
        right: 0,
        bottom: '-1px',
        padding: theme.spacing(2),
    },
});

export default function PrintPreview(props: PrintPreviewProps) {
    const {sidebar, layers, center, zoom, layerTypes, map} = props;

    const classes = useStyles();

    if (sidebar.type !== 'print' || !zoom || !center || !layerTypes || !map) {
        return <>{props.children}</>;
    }

    const canvas = map.getCanvas();
    const context = canvas && canvas.getContext ? canvas.getContext('webgl') : undefined;
    let maxCanvasSize = context ?
        context.MAX_RENDERBUFFER_SIZE / window.devicePixelRatio : undefined;

    if (!maxCanvasSize) return <>{props.children}</>;

    const {size, orientation, showCompass} = sidebar;
    const paper = pageSizes[size];
    maxCanvasSize = pxToPt(maxCanvasSize);

    // get scale width in px
    const glScale: HTMLElement | null = document.querySelector('.mapboxgl-ctrl-scale');
    const glScaleWidth = glScale ? glScale.offsetWidth : 0;

    const container = {
        // Subtract ~ sidebar width
        width: pxToPt(window.innerWidth - 300),
        // Subtract ~ header bar height + padding
        height: pxToPt(window.innerHeight - 105),
    };

    let previewPaperWidth;
    let previewPaperHeight;

    if (orientation === 'portrait') {
        previewPaperHeight = inToPt(paper.height);
        previewPaperWidth = inToPt(paper.width);

        if (previewPaperHeight > maxCanvasSize) {
            const reductionFactor = maxCanvasSize / previewPaperHeight;
            previewPaperHeight = maxCanvasSize;
            previewPaperWidth = previewPaperWidth * reductionFactor;
        }
    } else {
        previewPaperWidth = inToPt(paper.height);
        previewPaperHeight = inToPt(paper.width);

        if (previewPaperWidth > maxCanvasSize) {
            const reductionFactor = maxCanvasSize / previewPaperWidth;
            previewPaperWidth = maxCanvasSize;
            previewPaperHeight = previewPaperHeight * reductionFactor;
        }
    }

    const padding = ptToPx(pagePadding);
    const widthScaleF = (container.width - padding) / (previewPaperWidth + padding);
    const heightScaleF = (container.height - padding) / (previewPaperHeight + padding);

    let scale;
    if (
        (previewPaperWidth * widthScaleF) <= container.width &&
        (previewPaperHeight * widthScaleF) <= container.height
    ) {
        scale = widthScaleF;
    } else {
        scale = heightScaleF;
    }

    const translateX = Math.abs(Math.round(
        ((container.width - (padding * 2)) - (previewPaperWidth * scale)) / 2
    ));
    const translateY = Math.abs(Math.round((
        (container.height - (padding * 2)) - (previewPaperHeight * scale)) / 2
    ));
    const declination = getMagneticDeclination(center);

    return  <div className={classes.background}>
        <Paper
            elevation={1}
            style={{
                width: `${previewPaperWidth}pt`,
                height: `${previewPaperHeight}pt`,
                transform: `translate(${translateX}pt, ${translateY}pt) scale(${scale})`,
            }}
            className={`${classes.printPaper}`}
        >
            <div className={classes.paperInner} id="paperInner">
                <div style={{
                    position: 'absolute',
                    width: `calc(100% - ${pagePadding * 2}pt)`,
                    zIndex: 2,
                    padding: theme.spacing(1),
                }}>
                    <div style={{float: 'left'}}>
                        <img
                            src='/static/img/logos_icons/gaia-gps-logo-white-stroke.png'
                            style={{width: '120pt'}}
                        />
                    </div>
                    <div style={{float: 'right', textAlign: 'center'}}>
                        {showCompass && <div style={{
                            color: '#000000',
                            /* eslint-disable-next-line max-len */
                            textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff',
                            fontSize: '16px',
                            fontWeight: 600,
                            padding: theme.spacing(1),
                        }}>
                            <div style={{display: 'block'}}>
                                <Compass declination={declination} />
                            </div>
                            <div style={{
                                display: 'block',
                                marginTop: '4px',
                                width: '90px',
                                fontSize: '6pt'
                            }}>
                                Declination: {declination}&deg;
                            </div>

                        </div>}
                    </div>
                </div>

                <div style={{border: '3px solid #000000', height: '100%'}}>
                    {props.children}
                </div>

                <div className={classes.bottomBar}>
                    <div style={{float: 'left', marginLeft: theme.spacing(2)}}>
                        {layers && layerTypes && layers.map(l => {
                            return <PrintPreviewAttribution
                                key={l.type}
                                title={layerTypes[l.type].title}
                                attribution={layerTypes[l.type].attribution}
                            />;
                        })}
                    </div>

                    <div style={{
                        float: 'right',
                        marginRight: theme.spacing(4),
                        marginTop: theme.spacing(1),
                        textAlign: 'center'
                    }}>
                        <div style={{
                            width: `${glScaleWidth}px`,
                            borderBottom: '2px solid #000000',
                            borderLeft: '2px solid #000000',
                            borderRight: '2px solid #000000',
                            height: 8,
                        }}>
                            <Typography variant='caption' style={{
                                fontSize: '5pt',
                                position: 'absolute',
                                marginLeft: -5,
                                marginTop: -3,
                            }}>
                                {glScale ? glScale.innerText : ''}
                            </Typography>
                        </div>

                        <Typography variant='caption' style={{fontSize: '5pt'}}>
                            Web Mercator | EPSG: 3857
                        </Typography>
                    </div>
                </div>
            </div>
        </Paper>
    </div>;
}
